import { useSyncExternalStore } from "react";

export type AppEnv = "dev" | "prod";

const KEY = "support_env";
const listeners = new Set<() => void>();

// Named hostnames imply a fixed env that localStorage cannot override.
// Anything else (CloudFront *.cloudfront.net, localhost, etc) is "ambiguous"
// and falls back to localStorage / dev.
function envFromHostname(): AppEnv | null {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname;
  if (h.startsWith("support-prod")) return "prod";
  if (h.startsWith("support-dev")) return "dev";
  return null;
}

function safeLocalStorageGet(key: string): string | null {
  // localStorage can throw SecurityError when the user has blocked storage
  // for this origin (privacy mode / third-party cookies blocked / extensions).
  // Treat any failure as "no stored value" so the hostname default takes over.
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    // Same reasoning as the getter — ignore inability to persist.
  }
}

function read(): AppEnv {
  // Hostname-locked envs (support-prod / support-dev) win over any stale or
  // cross-polluted localStorage value. localStorage only applies when the
  // hostname is ambiguous (CloudFront default URL, localhost, etc.).
  const fromHost = envFromHostname();
  if (fromHost) return fromHost;
  const stored = safeLocalStorageGet(KEY);
  if (stored === "prod" || stored === "dev") return stored;
  return "dev";
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function setEnv(env: AppEnv) {
  if (read() === env) return;
  safeLocalStorageSet(KEY, env);
  for (const fn of listeners) fn();
}

export function useEnv(): AppEnv {
  return useSyncExternalStore(subscribe, read, () => "dev");
}

export function getEnv(): AppEnv {
  return read();
}
